import React, { useState } from 'react';
import { Loader2, Save, Eye, Send, Mail, Globe, ArrowLeft, EyeOff } from 'lucide-react';
import { newsletterApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { ImageListEditor, ButtonsEditor, SingleImagePicker } from './NewsletterEditorParts';
import { PreviewDialog, SendDialog } from './NewsletterDialogs';

const AUDS = [['members', 'Registered members'], ['contacts', 'Newsletter contacts']];
const blankAud = { subject: '', intro: '', banner: null, status: 'draft', stats: {} };

const Section = ({ title, hint, children, testId }) => (
  <div className="pbb-card p-4 sm:p-5 space-y-3" data-testid={testId}>
    <div><div className="font-semibold text-white">{title}</div>{hint && <div className="text-xs text-gray-300 mt-0.5">{hint}</div>}</div>
    {children}
  </div>
);

export const Results = ({ a }) => {
  const s = a.stats || {};
  const items = [['Eligible', s.eligible], ['Accepted for sending', s.accepted], ['Delivered', 'Not tracked'], ['Failed', s.failed], ['Waiting (daily limit)', s.queued], ['Unsubscribed', s.unsubscribed]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
      {items.map(([l, v]) => (
        <div key={l} className="bg-black/30 rounded p-2"><div className="text-gray-400">{l}</div><div className="text-white font-semibold">{v ?? 0}</div></div>
      ))}
    </div>
  );
};

function AudienceCard({ k, label, a, onPatch, onPreview, onTest, onSend, testing }) {
  const locked = a.status !== 'draft';
  return (
    <Section title={`${label} send`} testId={`nl-aud-${k}`}
      hint={locked ? `Status: ${a.status === 'sending' ? 'Sending' : 'Sent'}${a.startedAt ? ` · started ${new Date(a.startedAt).toLocaleString()}` : ''}` : 'Each audience gets its own top banner, subject line and short message.'}>
      <div className="text-xs text-gray-300">Top banner (emails only)</div>
      <SingleImagePicker value={a.banner} onChange={(v) => onPatch({ banner: v })} label="top banner" testId={`nl-banner-${k}`} disabled={locked} />
      <input value={a.subject} onChange={e => onPatch({ subject: e.target.value })} disabled={locked} maxLength={150}
        className="pbb-input" placeholder="Subject line" data-testid={`nl-subject-${k}`} />
      <div>
        <div className="text-xs text-gray-300 mb-1">Every email starts with <b className="text-white">"Hello [First Name],"</b> (or "Hello there,") automatically. Write your short message below.</div>
        <textarea rows={4} value={a.intro} onChange={e => onPatch({ intro: e.target.value })} disabled={locked} maxLength={3000}
          className="pbb-input" placeholder="Short introductory message" data-testid={`nl-intro-${k}`} />
      </div>
      {locked && <Results a={a} />}
      <div className="flex flex-wrap gap-2">
        <button onClick={onPreview} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm inline-flex items-center gap-1 text-gray-100" data-testid={`nl-preview-btn-${k}`}><Eye size={14} /> Preview</button>
        <button onClick={onTest} disabled={testing} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm inline-flex items-center gap-1 text-gray-100" data-testid={`nl-test-btn-${k}`}>
          {testing ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} Send test email
        </button>
        {!locked && <button onClick={onSend} className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold inline-flex items-center gap-1" data-testid={`nl-send-btn-${k}`}><Send size={14} /> Send to {label.toLowerCase()}</button>}
      </div>
    </Section>
  );
}

export default function NewsletterEditor({ campaign, onClose }) {
  const [c, setC] = useState(() => campaign || { name: '', contentImages: [], buttons: [], members: { ...blankAud }, contacts: { ...blankAud }, publishedAt: null });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sendAud, setSendAud] = useState(null);

  const patch = (p) => setC(prev => ({ ...prev, ...p }));
  const patchAud = (k, p) => setC(prev => ({ ...prev, [k]: { ...prev[k], ...p } }));

  const save = async (quiet) => {
    if (!c.name.trim()) { toast({ title: 'Give the newsletter a name first' }); return null; }
    setSaving(true);
    try {
      const body = { name: c.name.trim(), contentImages: c.contentImages, buttons: c.buttons,
        members: { subject: c.members.subject, intro: c.members.intro, banner: c.members.banner },
        contacts: { subject: c.contacts.subject, intro: c.contacts.intro, banner: c.contacts.banner },
        assetBase: process.env.REACT_APP_BACKEND_URL };
      const saved = c.id ? await newsletterApi.update(c.id, body) : await newsletterApi.create(body);
      setC(saved);
      if (!quiet) toast({ title: 'Draft saved' });
      return saved;
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Please try again.' });
      return null;
    } finally { setSaving(false); }
  };

  const withSave = async (fn) => { const s = await save(true); if (s) fn(s); };
  const test = (k) => withSave(async (s) => {
    setTesting(k);
    try { const r = await newsletterApi.test(s.id, k); toast({ title: 'Test email sent', description: `Sent to ${r.to}` }); }
    catch (err) { toast({ title: 'Test failed', description: err?.response?.data?.detail || 'Please try again.' }); }
    finally { setTesting(null); }
  });
  const togglePublish = () => withSave(async (s) => {
    try {
      if (s.publishedAt) { await newsletterApi.unpublish(s.id); setC({ ...s, publishedAt: null }); toast({ title: 'Removed from the app' }); }
      else { await newsletterApi.publish(s.id); setC({ ...s, publishedAt: new Date().toISOString() }); toast({ title: 'Published to the app', description: 'No emails were sent.' }); }
    } catch (err) { toast({ title: 'Publish failed', description: err?.response?.data?.detail || 'Please try again.' }); }
  });
  const reload = async () => { const all = await newsletterApi.list(); const me = all.find(x => x.id === c.id); if (me) setC(me); };

  return (
    <div className="space-y-4" data-testid="nl-editor">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-gray-300 hover:text-white" data-testid="nl-editor-back"><ArrowLeft size={16} /> All newsletters</button>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={togglePublish} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm inline-flex items-center gap-1 text-gray-100" data-testid="nl-publish-btn">
            {c.publishedAt ? <><EyeOff size={14} /> Unpublish from app</> : <><Globe size={14} /> Publish to app</>}
          </button>
          <button onClick={() => save(false)} disabled={saving} className="px-3 py-2 rounded-lg bg-white text-black text-sm font-semibold inline-flex items-center gap-1" data-testid="nl-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save draft
          </button>
        </div>
      </div>
      {c.publishedAt && <div className="text-xs text-green-300" data-testid="nl-published-note">Published in the app's Newsletter page since {new Date(c.publishedAt).toLocaleString()}. Publishing never sends emails.</div>}
      <Section title="Newsletter name" hint="Only you see this — it's used in your history and reports.">
        <input value={c.name} onChange={e => patch({ name: e.target.value })} maxLength={150} className="pbb-input" placeholder="e.g. Welcome Issue — June 2026" data-testid="nl-name" />
      </Section>
      <Section title="Newsletter content" hint="The same for everyone. Upload images and arrange their order." testId="nl-content-section">
        <ImageListEditor images={c.contentImages} onChange={(v) => patch({ contentImages: v })} />
      </Section>
      <Section title="Buttons & links (bottom of the newsletter)" hint="Shown below the content images in emails and in the app. Each button's clicks are counted." testId="nl-buttons-section">
        <ButtonsEditor buttons={c.buttons} onChange={(v) => patch({ buttons: v })} />
      </Section>
      <div className="grid lg:grid-cols-2 gap-4">
        {AUDS.map(([k, label]) => (
          <AudienceCard key={k} k={k} label={label} a={c[k]} testing={testing === k} onPatch={(p) => patchAud(k, p)}
            onPreview={() => withSave((s) => setPreview({ id: s.id, aud: k }))} onTest={() => test(k)}
            onSend={() => withSave((s) => setSendAud({ id: s.id, aud: k }))} />
        ))}
      </div>
      <PreviewDialog campaignId={preview?.id} audience={preview?.aud} onClose={() => setPreview(null)} />
      <SendDialog campaignId={sendAud?.id} audience={sendAud?.aud} onClose={() => setSendAud(null)} onSent={() => setTimeout(reload, 2500)} />
    </div>
  );
}
